import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';


import { HttpHeaders } from '@angular/common/http';
import { ApiService } from "../../api.service";
import { catchError } from "rxjs/operators";
import { TalentHappyPathAuthService } from "../shared/service/talent-happy-path-auth.service";

export interface ErrorResponse {
  error: string;
  errorId: string;
  errorDescription: string;
}

export function isErrorResponse(obj: any): obj is ErrorResponse {
  return typeof obj === 'object' && obj !== null && 'error' in obj;
}

@Injectable({
  providedIn: 'root'
})
export class QuickApplyApiService {
  constructor(private apiService: ApiService, private authService: TalentHappyPathAuthService) {
  }

  searchTalent(email: string): Observable<Talent> {
    return this.apiService.httpWrapper<Talent>({
      method: 'POST',
      url: '/api/quick-apply/search',
      body: email
    });
  }

  saveResume(file: File, talentId: number): Observable<ResumeDetail> {
    const formData = new FormData();
    formData.append('resumeFile', file);
    formData.append('talentId', String(talentId))
    const headers = new HttpHeaders().set('talentId', String(talentId));
    return this.apiService.httpWrapper<ResumeDetail>({
      method: 'POST',
      url: `/api/quick-apply/resume`,
      body: formData,
      headers: headers,
    });
  }

  /**
   * @param authenticated whether to offer the MyAquent portal credential on the call. The endpoint is
   *   public and the default is not to, which is right for reading a posting to display. Pass true
   *   where the ANSWER depends on who is asking — the video interview fields do, since BIZ-41058 —
   *   because the portal `Token` is added by `ApiService` only when this is set, while the funnel's
   *   `Bearer` rides on every call through the interceptor in `QuickApplyModule` regardless. Without
   *   it a talent who arrived from MyAquent and has no funnel sign-in is anonymous to the server here.
   */
  readPostingInfo(jobPostingId: string, authenticated = false): Observable<PostingInfo> {
    return this.apiService.httpWrapper<PostingInfo>({
      method: 'GET',
      url: `/api/quick-apply/posting/${jobPostingId}`,
      authenticated
    }).pipe(
      catchError((error) => {
        console.error('Error fetching posting info:', error);
        return of(null);
      })
    );
  }

  readPostingExtraInfo(jobPostingId: number): Observable<JobPostingExtraInfo> {
    return this.apiService.httpWrapper<JobPostingExtraInfo>({
      method: 'GET',
      url: `/api/quick-apply/posting-extra-info/${jobPostingId}`,
      authenticated: false
    }).pipe(
      catchError((error) => {
        console.error('Error fetching posting info:', error);
        return of(null);
      })
    );
  }


  apply(quickApplyInfo: QuickApplyInfo, files: File[] = []): Observable<Talent> {
    const formData = new FormData();
    formData.append('quickApplyInfo', new Blob([JSON.stringify(quickApplyInfo)], { type: 'application/json' }));
    files.forEach((file, index) => {
      formData.append('resumeFiles', file, file.name);
    });
    return this.apiService.httpWrapper<Talent>({
      method: 'POST',
      url: '/api/quick-apply/apply',
      body: formData
    });
  }

  readTalentLatestResume(talentPersonId: number): Observable<ResumeDetail> {
    return this.apiService.httpWrapper<ResumeDetail>({
      method: 'POST',
      url: '/api/resume/latest-resume',
      body: talentPersonId
    });
  }
}
